import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    CircularProgress,
    FormControl,
    MenuItem,
    Select,
    Tooltip,
    Typography
} from "@mui/material";
import {styled} from "@mui/system";
import {useLazyQuery} from "@apollo/client";

import SwapSessionTooltip from "./availability/SwapSessionTooltip";
import {SWAP_RATING_CONFIG_MAP} from "../domain/SwapRating";
import {SESSION_TYPE} from "../domain/SessionType";
import {toTitleCase} from "../helpers/TextUtils";
import {FETCH_SWAPABILITY} from "../queries/GraphQLQueries";

const popperStyle = {
    sx: {
        '& .MuiTooltip-tooltip': {
            borderRadius: '2rem',
            maxWidth: 'none'
        }
    }
};

const SessionSwappingOptionBox = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center'
}));

const SwapContentContainer = styled(Box)(() => ({
    width: 400,
    minHeight: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
}));

const SwapRatingBox = styled(Box)(() => ({
    display: 'inline-block',
    width: 20,
    height: 20,
    borderRadius: '50%',
    marginRight: 8
}));  
  
const SwapSessionFormControl = ({
    allSwappableTargetSessions,
    selectedTargetSession,
    handleSelectedTargetSession,
    sourceSessionToSwap
}) => {

    const [swapRatingIconHovered, setSwapRatingIconHovered] = useState(false);
    const [swappableSessionHealthReport, setSwappableSessionHealthReport] = useState([]);

    const [fetchSwapOptions, {loading: fetchingLatestSwapData, data: swapOptionsData}] = useLazyQuery(FETCH_SWAPABILITY, {fetchPolicy: 'network-only'});

    useEffect(() => {
        if (sourceSessionToSwap && swapRatingIconHovered) {
            fetchSwapOptions({variables: {sessionId: sourceSessionToSwap.id}})
        }
    }, [sourceSessionToSwap, fetchSwapOptions, swapRatingIconHovered]);

    useEffect(() => {
        if (!swapOptionsData || !swapOptionsData.swapability) {
            return;
        }

        const swappableSessions = swapOptionsData.swapability.map(performerSessionWithSwapRating => {
            return {
                targetShiftId: performerSessionWithSwapRating.targetShift.id,
                targetPerformerId: performerSessionWithSwapRating.targetShift.owner.id,
                targetPerformerName: performerSessionWithSwapRating.targetShift.owner.displayName,
                ruleReport: performerSessionWithSwapRating.ruleReport,
                swapRating: performerSessionWithSwapRating.swapRating,
            };
        });

        setSwappableSessionHealthReport(swappableSessions);
    }, [swapOptionsData]);

    const buildSwapSessionDetails = (session) => swappableSessionHealthReport?.length > 0 ?
      swappableSessionHealthReport.find(swap => swap.targetPerformerId === session.targetPerformerId)
      :
      allSwappableTargetSessions.find(swap => swap.targetPerformerId === session.targetPerformerId);

    const buildSwapRatingOptions = (session) => SWAP_RATING_CONFIG_MAP[buildSwapSessionDetails(session)?.swapRating].sortOrder || SWAP_RATING_CONFIG_MAP.UNKNOWN.sortOrder;

    const buildSortedPerformerListBasedOnSwapRating = useMemo(() => {
        return [...allSwappableTargetSessions].sort((a, b) => {
            const performerASwapRatingOrder = buildSwapRatingOptions(a);
            const performerBSwapRatingOrder = buildSwapRatingOptions(b);

            if (performerASwapRatingOrder !== performerBSwapRatingOrder) {
                return performerASwapRatingOrder - performerBSwapRatingOrder;
            }

            return a.targetPerformerName.localeCompare(b.targetPerformerName);
        });
    }, [allSwappableTargetSessions]);

    const populateTargetSessionTypeMessage = (session) => {
        const firstTargetSession = allSwappableTargetSessions.find(
            swappableSession => swappableSession.targetPerformerId === session?.targetPerformerId
        );

        let message = 'No Session';

        if (firstTargetSession?.targetSessionType === SESSION_TYPE.BREAK) {
            message = toTitleCase(firstTargetSession.targetSessionType);
        } else if (firstTargetSession?.targetSessionServiceType) {
            message = firstTargetSession.targetSessionServiceType;
        }

        return message;
    };

    return (
      <SwapContentContainer>
          {
              allSwappableTargetSessions.length > 0 ?
                <FormControl required sx={{minWidth: 400}}>
                    <Select
                      value={selectedTargetSession}
                      onChange={handleSelectedTargetSession}>
                        {
                            buildSortedPerformerListBasedOnSwapRating.map(session =>
                              <MenuItem key={session.targetPerformerId} value={session}>
                                  <SessionSwappingOptionBox>
                                      <Tooltip placement="left"
                                               title={
                                                   fetchingLatestSwapData
                                                     ?
                                                     <CircularProgress size={20} sx={{color: 'white'}}/>
                                                     :
                                                     <SwapSessionTooltip
                                                       swapSessionTooltipDetails={buildSwapSessionDetails(session)}
                                                       sourcePerformerName={sourceSessionToSwap.displayName}/>
                                               } PopperProps={popperStyle}>
                                          <SwapRatingBox
                                            sx={{
                                               backgroundColor: SWAP_RATING_CONFIG_MAP[buildSwapSessionDetails(session)?.swapRating]?.color
                                            }}
                                            session={session}
                                            onMouseEnter={() => setSwapRatingIconHovered(true)}
                                            onMouseLeave={() => setSwapRatingIconHovered(false)}/>
                                      </Tooltip>
                                      {`${session.targetPerformerName} - ${populateTargetSessionTypeMessage(session)}`}
                                  </SessionSwappingOptionBox>
                              </MenuItem>
                            )}
                    </Select>
                </FormControl>
                :
                <Typography variant="body1">{`No Performer available`}</Typography>
          }
      </SwapContentContainer>
    );
};

export default SwapSessionFormControl;